import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular';
import { PharmacyService } from 'src/app/shared/services/user/pharmacy.service';
import { RegistrationService } from 'src/app/shared/services/user/registration.service';
import { Address } from 'src/app/shared/models/dea';

export interface AddressVerificationAddress {
  line_1: string;
  line_2?: string;
  city: string;
  state: string;
  postal_code: string;
}

export type AddressVerificationResult =
  | { status: 'verified' }
  | { status: 'accepted'; address: AddressVerificationAddress }
  | { status: 'skipped' }
  | { status: 'idle' };

/**
 * Merges a Shippo correction into an address in place.
 * keepState: the state is fixed by the licence, so the correction must not change it.
 */
function applyCorrectedAddress(
  target: AddressVerificationAddress,
  corrected: AddressVerificationAddress,
  keepState = false,
): void {
  target.line_1 = corrected.line_1 || target.line_1;
  target.line_2 = corrected.line_2 ?? target.line_2;
  target.city = corrected.city || target.city;
  if (!keepState) {
    target.state = corrected.state || target.state;
  }
  target.postal_code = corrected.postal_code || target.postal_code;
}

/** Registration forms hold the licence-record shape (line1 / zip). */
function toVerificationAddress(address: Address): AddressVerificationAddress {
  return {
    line_1: address?.line1 || '',
    line_2: address?.line2 || '',
    city: address?.city || '',
    state: address?.state || '',
    postal_code: address?.zip || '',
  };
}

export function applyCorrectedRegistrationAddress(
  target: Address,
  corrected: AddressVerificationAddress,
  keepState = false,
): void {
  const merged = toVerificationAddress(target);
  applyCorrectedAddress(merged, corrected, keepState);
  target.line1 = merged.line_1;
  target.line2 = merged.line_2;
  target.city = merged.city;
  target.state = merged.state;
  target.zip = merged.postal_code;
}

/**
 * AddressVerificationComponent
 *
 * Drop-in USPS address verification widget. Pass an address and set `verify`
 * to `true` to trigger a verification call. The component emits a result via
 * `(onResult)` once the user has made a decision:
 *
 *   - status='verified' → address is good, no correction needed
 *   - status='accepted' → user accepted USPS suggestion; it is already merged into `address`
 *                         (corrected address also included in the result)
 *   - status='skipped'  → USPS couldn't find it, user chose to save anyway
 *   - status='idle'     → nothing happened yet
 *
 * Usage:
 *   <app-address-verification
 *     [address]="formData.delivery_address"
 *     [verify]="triggerVerify"
 *     (onResult)="handleVerificationResult($event)"
 *     (verifyChange)="triggerVerify = $event">
 *   </app-address-verification>
 */
@Component({
  selector: 'app-address-verification',
  templateUrl: './address-verification.component.html',
  styleUrls: ['./address-verification.component.scss']
})
export class AddressVerificationComponent implements OnChanges {
  /**
   * The address to verify. An accepted suggestion is merged into it in place.
   * Registration forms pass the licence-record shape (`line1` / `zip`).
   */
  @Input() address: AddressVerificationAddress | Address;

  /** Optional label shown in the panel header to identify which address is being verified. */
  @Input() label: string = '';

  /**
   * Set to `true` to kick off verification. The component resets it to
   * `false` via `(verifyChange)` once the call completes so two-way binding
   * works: `[(verify)]="triggerVerify"`.
   */
  @Input() verify: boolean = false;

  /** Registration forms: anonymous visitors (public route) and the licence-record address shape. */
  @Input() forRegistration = false;

  /** The state is fixed by the licence, so an accepted suggestion must not change it. */
  @Input() keepState = false;

  /** Emits the updated verify flag (supports two-way binding). */
  @Output() verifyChange = new EventEmitter<boolean>();

  /** Emits the final decision once the user has acted. */
  @Output() onResult = new EventEmitter<AddressVerificationResult>();

  // ── Internal state ──────────────────────────────────────────────────────
  /** The address as sent to Shippo; the suggestion panel compares against it. */
  sentAddress: AddressVerificationAddress | null = null;
  verifying = false;
  showSuggestion = false;
  suggestedAddress: AddressVerificationAddress | null = null;
  addressNotFound = false;
  addressNotFoundMessage = '';
  invalidZip = false;
  invalidZipMessage = '';

  constructor(
    private pharmacyService: PharmacyService,
    private registrationService: RegistrationService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['verify'] && this.verify && this.address) {
      this._runVerification();
    }
  }

  /** A check is running or a panel is waiting for the user; hosts disable their submit meanwhile. */
  get active(): boolean {
    return this.verifying || this.showSuggestion || this.addressNotFound || this.invalidZip;
  }

  // ── Public helpers used by the template ─────────────────────────────────

  fieldsDiffer(field: keyof AddressVerificationAddress): boolean {
    if (!this.suggestedAddress || !this.sentAddress) return false;
    const current = (this.sentAddress[field] || '').toString().trim().toUpperCase();
    const suggested = (this.suggestedAddress[field] || '').toString().trim().toUpperCase();
    return current !== suggested && suggested !== '';
  }

  // ── User actions ─────────────────────────────────────────────────────────

  acceptSuggestion() {
    const accepted = { ...this.suggestedAddress } as AddressVerificationAddress;
    if (this.forRegistration) {
      applyCorrectedRegistrationAddress(this.address as Address, accepted, this.keepState);
    } else {
      applyCorrectedAddress(this.address as AddressVerificationAddress, accepted, this.keepState);
    }
    this._reset();
    this.onResult.emit({ status: 'accepted', address: accepted });
  }

  keepOriginal() {
    this._reset();
    this.onResult.emit({ status: 'verified' });
  }

  saveAnyway() {
    this._reset();
    this.onResult.emit({ status: 'skipped' });
  }

  editAddress() {
    this._reset();
    // No result emitted — the user wants to go back and correct the address.
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private _runVerification() {
    this.invalidZip = false;
    this.invalidZipMessage = '';
    this.addressNotFound = false;
    this.addressNotFoundMessage = '';
    this.showSuggestion = false;
    this.suggestedAddress = null;

    const address = this.address as AddressVerificationAddress;
    this.sentAddress = this.forRegistration
      ? toVerificationAddress(this.address as Address)
      : {
          line_1: address.line_1,
          line_2: address.line_2,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
        };

    // ZIP or ZIP+4; the backend trims to ZIP5 before calling Shippo.
    const zip = (this.sentAddress.postal_code || '').trim();
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      this.invalidZip = true;
      this.invalidZipMessage = zip.length === 0
        ? 'Postal code is required.'
        : 'Postal code must be 5 digits or ZIP+4 (e.g. 85001 or 85001-1234).';
      Promise.resolve().then(() => this._done()); // emit after this change-detection pass
      return;
    }

    this.verifying = true;

    const verify$ = this.forRegistration
      ? this.registrationService.verifyAddress(this.sentAddress)
      : this.pharmacyService.verifyAddress(this.sentAddress);

    verify$.subscribe(
      (result) => {
        this.verifying = false;
        this._done();
        const data = result?.data;

        if (data?.address_not_found) {
          this.addressNotFound = true;
          this.addressNotFoundMessage = data.error || 'USPS could not verify this address.';
        } else if (data?.correction && data?.corrected_address) {
          this.suggestedAddress = data.corrected_address as AddressVerificationAddress;
          this.showSuggestion = true;
        } else {
          // Valid — no correction needed
          this.onResult.emit({ status: 'verified' });
        }
      },
      () => {
        // Service unreachable — treat as verified so the user isn't blocked
        this.verifying = false;
        this._done();
        this.onResult.emit({ status: 'verified' });
      }
    );
  }

  private _done() {
    this.verifyChange.emit(false);
  }

  private _reset() {
    this.verifying = false;
    this.showSuggestion = false;
    this.suggestedAddress = null;
    this.addressNotFound = false;
    this.addressNotFoundMessage = '';
    this.invalidZip = false;
    this.invalidZipMessage = '';
    this._done();
  }
}

@NgModule({
  declarations: [AddressVerificationComponent],
  imports: [CommonModule, DxButtonModule],
  exports: [AddressVerificationComponent]
})
export class AddressVerificationModule {}
